import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Spin, Typography } from 'antd';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { getNamespaceGatewayGapsApi } from '../../../api/customerApi';
import { DECODED_ACCESS_TOKEN } from '../../../constants/system.keys';
import storageService from '../../../services/storage.service';

const { Text } = Typography;

const getBarColor = (percent) => {
    if (percent < 80) return '#ef4444'; // Đỏ (Rất thấp)
    if (percent < 95) return '#f59e0b'; // Cam / Vàng (Cảnh báo)
    return '#0e9347'; // Xanh lá (Tốt)
};

const CustomGatewayTooltip = ({ active, payload, t }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div
                style={{
                    backgroundColor: '#1f1f1f',
                    padding: '10px 14px',
                    borderRadius: '6px',
                    border: '1px solid #333',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                    color: '#fff',
                    maxWidth: '320px',
                }}
            >
                <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#60a5fa', marginBottom: '4px' }}>
                    {data.gatewayName || data.gatewaySerial}
                </div>
                <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '6px' }}>
                    Serial: {data.gatewaySerial}
                </div>
                <div style={{ fontSize: '13px', marginBottom: '2px' }}>
                    {t('Upload Rate')}:{' '}
                    <strong style={{ color: getBarColor(data.uploadedPercent) }}>
                        {data.uploadedPercent}%
                    </strong>
                </div>
                <div style={{ fontSize: '12px', color: '#ccc' }}>
                    {t('Online camera count')}: {data.cameraOnline}/{data.cameraTotal} {t('camera')}
                </div>
                <div style={{ fontSize: '12px', color: '#ccc' }}>
                    {t('Gap duration (missing)')}: {data.totalGapFormatted || `${data.totalGapS}s`}
                </div>
            </div>
        );
    }
    return null;
};

const BarLabel = (props) => {
    const { x, y, width, height, value } = props;
    if (value === undefined || value === null) return null;
    return (
        <text
            x={x + width + 8}
            y={y + height / 2 + 4}
            fill="#e5e7eb"
            fontSize="11"
            fontWeight="600"
            textAnchor="start"
        >
            {`${value}%`}
        </text>
    );
};

const GatewaySLA = ({ dateRanger, gateways: propGateways }) => {
    const { t } = useTranslation();
    const { namespace } = useSelector((state) => state);
    const [gatewaysData, setGatewaysData] = useState([]);
    const [loading, setLoading] = useState(false);

    const fromDateStr = useMemo(() => {
        if (!dateRanger || !dateRanger[0]) return moment().subtract(30, 'days').format('YYYY-MM-DD');
        return moment(dateRanger[0]).format('YYYY-MM-DD');
    }, [dateRanger]);

    const toDateStr = useMemo(() => {
        if (!dateRanger || !dateRanger[1]) return moment().format('YYYY-MM-DD');
        return moment(dateRanger[1]).format('YYYY-MM-DD');
    }, [dateRanger]);

    const totalDays = useMemo(() => {
        if (!dateRanger || !dateRanger[0] || !dateRanger[1]) return 30;
        const start = moment(dateRanger[0]).startOf('day');
        const end = moment(dateRanger[1]).endOf('day');
        const diff = end.diff(start, 'days') + 1;
        return diff > 0 ? diff : 1;
    }, [dateRanger]);

    useEffect(() => {
        const fetchGatewayGaps = async () => {
            if (!namespace) return;
            setLoading(true);
            try {
                const token = storageService.getInternalStorageByKey(DECODED_ACCESS_TOKEN);
                const dataQuery = {
                    fromDate: fromDateStr,
                    toDate: toDateStr,
                };
                const res = await getNamespaceGatewayGapsApi(token, { namespace }, dataQuery);
                if (res && res.data) {
                    const apiData = res.data.data || res.data;
                    setGatewaysData(apiData.gateways || (Array.isArray(apiData) ? apiData : []));
                } else {
                    setGatewaysData([]);
                }
            } catch (err) {
                console.error('Error fetching gateway gaps:', err);
                setGatewaysData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchGatewayGaps();
    }, [namespace, fromDateStr, toDateStr]);

    const effectiveGateways = propGateways && propGateways.length > 0 ? propGateways : gatewaysData;

    const top20LowestGateways = useMemo(() => {
        if (!effectiveGateways || !Array.isArray(effectiveGateways) || effectiveGateways.length === 0) return [];

        const calculated = effectiveGateways
            .map((gw) => {
                const camerasCount = gw.cameraOnline > 0 ? gw.cameraOnline : (gw.cameraTotal || 0);
                const totalRequiredSeconds = camerasCount * totalDays * 86400;
                const totalGapS = gw.totalGapS || 0;

                let uploadedPercent = 100;
                if (totalRequiredSeconds > 0) {
                    const totalUploadedSeconds = Math.max(0, totalRequiredSeconds - totalGapS);
                    uploadedPercent = Number(((totalUploadedSeconds / totalRequiredSeconds) * 100).toFixed(2));
                    if (uploadedPercent > 100) uploadedPercent = 100;
                    if (uploadedPercent < 0) uploadedPercent = 0;
                } else if (totalGapS > 0) {
                    uploadedPercent = 0;
                }

                let displayName = gw.gatewayName || gw.gatewaySerial || 'N/A';
                if (displayName.length > 32) {
                    displayName = `${displayName.substring(0, 30)}...`;
                }

                return {
                    ...gw,
                    camerasCount,
                    uploadedPercent,
                    displayName,
                };
            })
            .filter((gw) => gw.cameraTotal > 0 || gw.cameraOnline > 0 || gw.totalGapS > 0);

        // Sắp xếp tăng dần theo uploadedPercent (tỉ lệ upload thấp nhất lên đầu)
        calculated.sort((a, b) => a.uploadedPercent - b.uploadedPercent);

        // Lấy top 20
        return calculated.slice(0, 20);
    }, [effectiveGateways, totalDays]);

    const chartHeight = Math.max(360, top20LowestGateways.length * 30 + 40);

    if (namespace !== "hdbank.com.vn") return null;

    return (
        <div
            className="chart-item"
            style={{
                width: '100%',
                marginTop: '16px',
                backgroundColor: '#141414',
                borderRadius: '8px',
                padding: '16px 20px',
                border: '1px solid #262626',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justify: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                }}
            >
                <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#fff' }}>
                    {t('TOP 20 GATEWAYS WITH LOWEST UPLOAD RATE')}
                </Text>
            </div>

            {loading ? (
                <div style={{ height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spin size="default" />
                </div>
            ) : top20LowestGateways.length === 0 ? (
                <Empty description={t('No Gateway Data')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart
                        layout="vertical"
                        data={top20LowestGateways}
                        margin={{ top: 10, right: 50, left: 10, bottom: 10 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#262626" />
                        <XAxis
                            type="number"
                            domain={[0, 100]}
                            tick={{ fill: '#8c8c8c', fontSize: 12 }}
                            tickFormatter={(val) => `${val}%`}
                            stroke="#434343"
                        />
                        <YAxis
                            type="category"
                            dataKey="displayName"
                            width={260}
                            tick={{ fill: '#d9d9d9', fontSize: 12 }}
                            stroke="#434343"
                        />
                        <Tooltip content={<CustomGatewayTooltip t={t} />} cursor={{ fill: 'transparent' }} />
                        <Bar dataKey="uploadedPercent" barSize={16} radius={[0, 4, 4, 0]}>
                            {top20LowestGateways.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={getBarColor(entry.uploadedPercent)} />
                            ))}
                            <LabelList dataKey="uploadedPercent" content={<BarLabel />} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

export default GatewaySLA;
